import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Card, CardBody, CardHeader, Code,
  FormControl, FormHelperText, FormLabel, HStack, Heading, Input, Select,
  Spinner, Switch, Text, VStack, SimpleGrid, Table, Thead, Tbody,
  Tr, Th, Td, TableContainer, IconButton, OrderedList, ListItem, useToast,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useDisclosure,
} from '@chakra-ui/react';
import { FiCopy, FiExternalLink, FiRefreshCw } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import PageShell from '../components/PageShell';
import bookingWidgetService from '../services/bookingWidgetService';

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;
const THEMES = ['modern', 'classic', 'minimal', 'elegant'];
const PLATFORMS = ['simple_html', 'wordpress', 'wix', 'squarespace'];
const BOOKING_PAGE_URL_STORAGE_KEY = 'admine_booking_widget_booking_page_url';

const readStoredBookingPageUrl = () => {
  try { return localStorage.getItem(BOOKING_PAGE_URL_STORAGE_KEY) || ''; }
  catch { return ''; }
};
const storeBookingPageUrl = (value) => {
  try { localStorage.setItem(BOOKING_PAGE_URL_STORAGE_KEY, value); }
  catch { /* private mode — fine, it's just a convenience */ }
};

// The ONE place the embed loader URL / hosted booking page host is
// defined on this page — both come from the backend (same constant the
// API uses), never hardcoded twice.
const buildSnippet = ({ widgetLoaderUrl, apiKey, theme, primaryColor, secondaryColor, roomType, currency }) => {
  const attrs = [
    'data-room99-widget',
    `data-api-key="${apiKey}"`,
    `data-theme="${theme}"`,
    `data-primary-color="${primaryColor}"`,
    `data-secondary-color="${secondaryColor}"`,
  ];
  if (roomType) attrs.push(`data-room-type="${roomType}"`);
  if (currency) attrs.push(`data-currency="${currency}"`);
  return `<div ${attrs.join(' ')}></div>\n<script src="${widgetLoaderUrl}" async></script>`;
};

const BookingWidget = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState('');

  const [theme, setTheme] = useState('modern');
  const [primaryColor, setPrimaryColor] = useState('#007bff');
  const [secondaryColor, setSecondaryColor] = useState('#6c757d');
  const [widgetDefaultCurrency, setWidgetDefaultCurrency] = useState('');
  const [widgetDefaultLanguage, setWidgetDefaultLanguage] = useState('');
  const [successUrl, setSuccessUrl] = useState('');
  const [isActive, setIsActive] = useState(true);

  // "Get the code" generator inputs — not saved, just steer the snippet
  // shown below.
  const [platform, setPlatform] = useState('simple_html');
  const [snippetRoomType, setSnippetRoomType] = useState('');
  const [snippetCurrency, setSnippetCurrency] = useState('');

  // Per-room link generator — the hotelier's own booking page address.
  // Kept client-side only (per-browser convenience); not part of the
  // saved widget settings.
  const [bookingPageUrl, setBookingPageUrl] = useState(readStoredBookingPageUrl);

  const regenerateDialog = useDisclosure();

  const load = async () => {
    try {
      setLoading(true);
      const res = await bookingWidgetService.getSettings();
      setData(res);
      setTheme(res.theme || 'modern');
      setPrimaryColor(res.primaryColor || '#007bff');
      setSecondaryColor(res.secondaryColor || '#6c757d');
      setWidgetDefaultCurrency(res.widgetDefaultCurrency || '');
      setWidgetDefaultLanguage(res.widgetDefaultLanguage || '');
      setSuccessUrl(res.successUrl || '');
      setIsActive(res.isActive !== false);
      setError('');
    } catch {
      setError(t('bookingWidget.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const primaryColorInvalid = primaryColor !== '' && !HEX_RE.test(primaryColor);
  const secondaryColorInvalid = secondaryColor !== '' && !HEX_RE.test(secondaryColor);

  const save = async () => {
    if (primaryColorInvalid || secondaryColorInvalid) {
      toast({ title: t('bookingWidget.toasts.checkFields'), status: 'warning', duration: 4000 });
      return;
    }
    try {
      setSaving(true);
      const res = await bookingWidgetService.updateSettings({
        theme, primaryColor, secondaryColor,
        widgetDefaultCurrency, widgetDefaultLanguage,
        successUrl, isActive,
      });
      setData(res);
      toast({ title: t('bookingWidget.toasts.saved'), status: 'success', duration: 2500 });
    } catch (e) {
      toast({
        title: t('bookingWidget.toasts.saveFailed'),
        description: e?.response?.data?.detail || t('bookingWidget.toasts.checkFields'),
        status: 'error', duration: 6000, isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const regenerateKey = async () => {
    try {
      setRegenerating(true);
      const res = await bookingWidgetService.regenerateKey();
      setData(res);
      toast({ title: t('bookingWidget.toasts.keyRegenerated'), status: 'info', duration: 8000, isClosable: true });
    } catch (e) {
      toast({
        title: t('bookingWidget.toasts.saveFailed'),
        description: e?.response?.data?.detail,
        status: 'error', duration: 6000, isClosable: true,
      });
    } finally {
      setRegenerating(false);
      regenerateDialog.onClose();
    }
  };

  // navigator.clipboard is unavailable (or its write silently rejects) over
  // plain http and inside some sandboxed iframes/previews, so a bare
  // writeText() call can fail without the hotelier ever knowing their
  // "Copy" click did nothing. Fall back to the classic
  // select-a-hidden-textarea-and-execCommand trick, and only ever show the
  // "Copied" toast once a copy actually happened.
  const fallbackCopy = (value) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  };

  const copy = (value, label) => {
    const reportResult = (ok) => {
      if (ok) {
        toast({ title: label || t('bookingWidget.toasts.copied'), status: 'success', duration: 1500 });
      } else {
        toast({ title: t('bookingWidget.toasts.copyFailed'), status: 'error', duration: 4000 });
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(
        () => reportResult(true),
        () => reportResult(fallbackCopy(value)),
      );
    } else {
      reportResult(fallbackCopy(value));
    }
  };

  const onBookingPageUrlChange = (e) => {
    const value = e.target.value;
    setBookingPageUrl(value);
    storeBookingPageUrl(value);
  };

  const snippet = useMemo(() => {
    if (!data) return '';
    return buildSnippet({
      widgetLoaderUrl: data.widgetLoaderUrl,
      apiKey: data.apiKey,
      theme, primaryColor, secondaryColor,
      roomType: snippetRoomType,
      currency: snippetCurrency,
    });
  }, [data, theme, primaryColor, secondaryColor, snippetRoomType, snippetCurrency]);

  const platformSteps = (data && t(`bookingWidget.code.steps.${platform}`, { returnObjects: true })) || [];

  const previewUrl = (roomTypeCode) => {
    if (!data) return '#';
    const params = new URLSearchParams({ api_key: data.apiKey });
    if (roomTypeCode) params.set('room_type', roomTypeCode);
    return `${data.hostedBookingPageUrl}/?${params.toString()}`;
  };

  const roomLinkFor = (roomTypeCode) => {
    const query = `?room_type=${encodeURIComponent(roomTypeCode)}`;
    const base = (bookingPageUrl || '').trim().replace(/\/+$/, '');
    return base ? `${base}${query}` : query;
  };

  if (loading) {
    return (
      <PageShell title={t('bookingWidget.page.title')}>
        <HStack py={10} justify="center"><Spinner /></HStack>
      </PageShell>
    );
  }
  if (error) {
    return (
      <PageShell title={t('bookingWidget.page.title')}>
        <Alert status="error"><AlertIcon />{error}</Alert>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={t('bookingWidget.page.title')}
      subtitle={t('bookingWidget.page.subtitle')}
    >
      <VStack align="stretch" spacing={4} maxW="900px">
        {/* API key */}
        <Card>
          <CardHeader pb={0}><Heading size="sm">{t('bookingWidget.apiKey.title')}</Heading></CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm" color="gray.600">{t('bookingWidget.apiKey.help')}</Text>
              <HStack flexWrap="wrap">
                <Code fontSize="sm" p={2} wordBreak="break-all">{data?.apiKey}</Code>
                <IconButton size="sm" aria-label={t('bookingWidget.code.copy')} icon={<FiCopy />}
                  onClick={() => copy(data?.apiKey, t('bookingWidget.toasts.copied'))} />
                <Button size="sm" leftIcon={<FiRefreshCw />} variant="outline" isLoading={regenerating}
                  onClick={regenerateDialog.onOpen}>
                  {t('bookingWidget.apiKey.regenerate')}
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Settings */}
        <Card>
          <CardHeader pb={0}><Heading size="sm">{t('bookingWidget.settings.title')}</Heading></CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">{t('bookingWidget.settings.theme')}</FormLabel>
                  <Select size="sm" value={theme} onChange={(e) => setTheme(e.target.value)}>
                    {THEMES.map((th) => (
                      <option key={th} value={th}>{t(`bookingWidget.settings.themeOptions.${th}`)}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl display="flex" alignItems="center" gap={3} mt={{ base: 0, md: 6 }}>
                  <Switch isChecked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                  <Box>
                    <FormLabel mb={0} fontSize="sm">{t('bookingWidget.settings.active')}</FormLabel>
                    <FormHelperText fontSize="xs" mt={0}>{t('bookingWidget.settings.activeHelp')}</FormHelperText>
                  </Box>
                </FormControl>

                <FormControl isInvalid={primaryColorInvalid}>
                  <FormLabel fontSize="sm">{t('bookingWidget.settings.primaryColor')}</FormLabel>
                  <HStack>
                    <Input size="sm" type="color" w="48px" p={0.5}
                      value={HEX_RE.test(primaryColor) ? primaryColor : '#007bff'}
                      onChange={(e) => setPrimaryColor(e.target.value)} />
                    <Input size="sm" maxW="140px" value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)} placeholder="#007bff" />
                  </HStack>
                  {primaryColorInvalid && <FormHelperText color="red.500" fontSize="xs">{t('bookingWidget.settings.colorInvalid')}</FormHelperText>}
                </FormControl>

                <FormControl isInvalid={secondaryColorInvalid}>
                  <FormLabel fontSize="sm">{t('bookingWidget.settings.secondaryColor')}</FormLabel>
                  <HStack>
                    <Input size="sm" type="color" w="48px" p={0.5}
                      value={HEX_RE.test(secondaryColor) ? secondaryColor : '#6c757d'}
                      onChange={(e) => setSecondaryColor(e.target.value)} />
                    <Input size="sm" maxW="140px" value={secondaryColor}
                      onChange={(e) => setSecondaryColor(e.target.value)} placeholder="#6c757d" />
                  </HStack>
                  {secondaryColorInvalid && <FormHelperText color="red.500" fontSize="xs">{t('bookingWidget.settings.colorInvalid')}</FormHelperText>}
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">{t('bookingWidget.settings.defaultCurrency')}</FormLabel>
                  <Select size="sm" value={widgetDefaultCurrency} onChange={(e) => setWidgetDefaultCurrency(e.target.value)}>
                    <option value="">{t('bookingWidget.settings.defaultCurrencyHotel')}</option>
                    {(data?.supportedCurrencies || []).map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                  <FormHelperText fontSize="xs">{t('bookingWidget.settings.defaultCurrencyHelp')}</FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">{t('bookingWidget.settings.defaultLanguage')}</FormLabel>
                  <Select size="sm" value={widgetDefaultLanguage} onChange={(e) => setWidgetDefaultLanguage(e.target.value)}>
                    <option value="">{t('bookingWidget.settings.defaultLanguageBrowser')}</option>
                    <option value="en">English</option>
                    <option value="es">Español</option>
                    <option value="fr">Français</option>
                    <option value="de">Deutsch</option>
                    <option value="pt">Português</option>
                    <option value="it">Italiano</option>
                    <option value="id">Bahasa Indonesia</option>
                    <option value="th">ไทย</option>
                    <option value="lt">Lietuvių</option>
                    <option value="fil">Filipino</option>
                  </Select>
                  <FormHelperText fontSize="xs">{t('bookingWidget.settings.defaultLanguageHelp')}</FormHelperText>
                </FormControl>

                <FormControl gridColumn={{ md: '1 / -1' }}>
                  <FormLabel fontSize="sm">{t('bookingWidget.settings.successUrl')}</FormLabel>
                  <Input size="sm" value={successUrl} onChange={(e) => setSuccessUrl(e.target.value)}
                    placeholder={t('bookingWidget.settings.successUrlPlaceholder')} />
                  <FormHelperText fontSize="xs">{t('bookingWidget.settings.successUrlHelp')}</FormHelperText>
                </FormControl>
              </SimpleGrid>

              <HStack justify="flex-end">
                <Button colorScheme="blue" onClick={save} isLoading={saving}>{t('bookingWidget.settings.save')}</Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Get the code */}
        <Card>
          <CardHeader pb={0}>
            <Heading size="sm">{t('bookingWidget.code.title')}</Heading>
            <Text fontSize="sm" color="gray.600" mt={1}>{t('bookingWidget.code.intro')}</Text>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">{t('bookingWidget.code.platformLabel')}</FormLabel>
                  <Select size="sm" value={platform} onChange={(e) => setPlatform(e.target.value)}>
                    {PLATFORMS.map((p) => (
                      <option key={p} value={p}>{t(`bookingWidget.code.platforms.${p}`)}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('bookingWidget.code.openRoomTypeLabel')}</FormLabel>
                  <Select size="sm" value={snippetRoomType} onChange={(e) => setSnippetRoomType(e.target.value)}>
                    <option value="">{t('bookingWidget.code.openRoomTypeNone')}</option>
                    {(data?.roomTypes || []).map((rt) => (
                      <option key={rt.id} value={rt.code}>{rt.name}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">{t('bookingWidget.code.prefillCurrencyLabel')}</FormLabel>
                  <Select size="sm" value={snippetCurrency} onChange={(e) => setSnippetCurrency(e.target.value)}>
                    <option value="">{t('bookingWidget.code.prefillCurrencyNone')}</option>
                    {(data?.supportedCurrencies || []).map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </FormControl>
              </SimpleGrid>

              <Box>
                <HStack justify="space-between" mb={1}>
                  <Text fontSize="sm" fontWeight="600">{t('bookingWidget.code.snippetLabel')}</Text>
                  <Button size="xs" leftIcon={<FiCopy />} onClick={() => copy(snippet, t('bookingWidget.toasts.copied'))}>
                    {t('bookingWidget.code.copy')}
                  </Button>
                </HStack>
                <Code
                  as="pre"
                  display="block"
                  fontSize="xs"
                  p={3}
                  borderRadius="md"
                  whiteSpace="pre-wrap"
                  wordBreak="break-all"
                  overflowX="auto"
                  maxW="100%"
                >
                  {snippet}
                </Code>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="600" mb={2}>
                  {t(`bookingWidget.code.platforms.${platform}`)}
                </Text>
                <OrderedList fontSize="sm" color="gray.700" spacing={1} pl={2}>
                  {platformSteps.map((step, i) => <ListItem key={i}>{step}</ListItem>)}
                </OrderedList>
              </Box>
            </VStack>
          </CardBody>
        </Card>

        {/* Per-room links */}
        <Card>
          <CardHeader pb={0}>
            <Heading size="sm">{t('bookingWidget.links.title')}</Heading>
            <Text fontSize="sm" color="gray.600" mt={1}>{t('bookingWidget.links.intro')}</Text>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <FormControl maxW="420px">
                <FormLabel fontSize="sm">{t('bookingWidget.links.bookingPageUrlLabel')}</FormLabel>
                <Input size="sm" value={bookingPageUrl} onChange={onBookingPageUrlChange}
                  placeholder={t('bookingWidget.links.bookingPageUrlPlaceholder')} />
                <FormHelperText fontSize="xs">{t('bookingWidget.links.bookingPageUrlHelp')}</FormHelperText>
              </FormControl>

              {(data?.roomTypes || []).length === 0 ? (
                <Alert status="info" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  {t('bookingWidget.links.empty')}
                </Alert>
              ) : (
                <TableContainer overflowX="auto">
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>{t('bookingWidget.links.table.roomType')}</Th>
                        <Th>{t('bookingWidget.links.table.code')}</Th>
                        <Th>{t('bookingWidget.links.table.link')}</Th>
                        <Th />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(data?.roomTypes || []).map((rt) => (
                        <Tr key={rt.id}>
                          <Td>{rt.name}</Td>
                          <Td><Badge>{rt.code}</Badge></Td>
                          <Td maxW="320px">
                            <Code fontSize="xs" p={1} wordBreak="break-all" whiteSpace="pre-wrap">
                              {roomLinkFor(rt.code)}
                            </Code>
                          </Td>
                          <Td>
                            <HStack spacing={1}>
                              <IconButton size="xs" aria-label={t('bookingWidget.links.table.copy')} icon={<FiCopy />}
                                onClick={() => copy(roomLinkFor(rt.code), t('bookingWidget.toasts.copied'))} />
                              <IconButton size="xs" aria-label={t('bookingWidget.links.table.preview')} icon={<FiExternalLink />}
                                as="a" href={previewUrl(rt.code)} target="_blank" rel="noreferrer" />
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      <AlertDialog isOpen={regenerateDialog.isOpen} onClose={regenerateDialog.onClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>{t('bookingWidget.apiKey.regenerateConfirmTitle')}</AlertDialogHeader>
            <AlertDialogBody>{t('bookingWidget.apiKey.regenerateConfirmBody')}</AlertDialogBody>
            <AlertDialogFooter>
              <Button variant="ghost" onClick={regenerateDialog.onClose}>{t('common.cancel')}</Button>
              <Button colorScheme="red" ml={3} onClick={regenerateKey} isLoading={regenerating}>
                {t('bookingWidget.apiKey.regenerate')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </PageShell>
  );
};

export default BookingWidget;
